<template>
  <div class="package-panel">
    <slot name="summary-header" />

    <ul class="package-panel__products">
      <li
        class="package-panel__option"
        v-for="product in filteredSelectedProducts"
        :key="product.code"
        :class="{ disabled: isCellDisabled(product) }"
        data-testid="package-panel-option"
      >
        <!-- Left: icon -->
        <div class="package-panel__option-image-wrapper">
          <img
            v-if="
              product.type !== 'Broadband' || !product.code.includes('_NONE')
            "
            class="package-panel__option-image"
            :src="productImage(product)"
            :alt="product.title"
          />
          <i
            v-else-if="
              product.type === 'Broadband' && product.code.includes('_NONE')
            "
            class="fa-solid fa-wifi-slash no-broadband-icon"
            title="No broadband selected"
          ></i>
        </div>

        <!-- Middle: title + price -->
        <div class="package-panel__option-details">
          <div class="package-panel__option-header">
            <h1 class="package-panel__option-name" :data-testid="product.type">
              {{ product.title }}
            </h1>
            <InfoBubble
              :text="productInfoLabel(product)"
              v-if="product.type !== 'Broadband'"
              iconColor="#3B4143"
            />
          </div>
          <p
            v-if="product.optional || product.type.includes('Broadband')"
            class="package-panel__option-price"
            :data-testid="'current-price-' + product.type"
          >
            <template
              v-if="
                product.type.includes('Broadband') &&
                  isDefaultBroadbandProductOptionSelected
              "
            >
              <span
                class="package-panel__option-price-included"
                data-testid="broadband-included-in-package"
                >Included in package</span
              >
            </template>
            <template v-else-if="!product.code.includes('_NONE')">
              +£{{ formatBroadbandDeltaPrice(product).toFixed(2) }} per person
              per week
            </template>
          </p>
        </div>

        <div v-if="product.type.includes('Free Contents Insurance')">
          <span
            v-if="product.type.includes('Free Contents Insurance')"
            class="package-panel__free-pill"
          >
            FREE
          </span>
        </div>

        <div
          v-if="product.optional || product.type.includes('Broadband')"
          class="package-panel__option-action"
        >
          <i
            v-if="product.type.includes('Broadband')"
            class="fa-solid fa-square-pen edit-broadband"
            data-testid="edit-broadband"
            @click="openModal(product)"
          ></i>

          <input
            v-else
            type="checkbox"
            class="form-checkbox"
            :checked="product.enabled"
            v-on:change="updateProductSelection(product)"
            :data-testid="'checkbox_' + product.type"
          />
        </div>
      </li>
    </ul>

    <BroadbandSelectorModal
      :visible="showModal"
      :products="broadbandProducts"
      :selected="selectedBroadband"
      @option-selected="updateSelected"
      @price-changed="priceChanged"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import InfoBubble from "@/components/InfoBubble.vue";
import BroadbandSelectorModal from "@/components/modals/BroadbandSelectorModal.vue";

export default {
  name: "PackageSummaryNew",
  components: { InfoBubble, BroadbandSelectorModal },
  data() {
    return {
      infoPanelShowing: false,
      openInfoProductCode: null,
      errorResponse: null,
      showModal: false,
      selectedProduct: null,
    };
  },
  props: {
    thin: {
      type: Boolean,
      required: false,
      default: false,
    },
    joiningTenant: {
      type: Boolean,
      required: false,
      default: false,
    },
    inlineEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
    onToggleProducts: {
      type: Function,
      default: () => {},
    },
    showOptionalProducts: {
      type: Boolean,
      default: () => true,
    },
    onToggleTvLicence: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    ...mapState(["pickedProductPriceCodes"]),
    ...mapState({
      products({ products }) {
        return products;
      },
      selectedProducts({ accountInformation }) {
        if (this.joiningTenant) {
          return accountInformation.products;
        } else {
          return this.showOptionalProducts
            ? this.$store.getters.selectedUnselectedProducts
            : this.$store.getters.selectedProducts;
        }
      },
      broadbandProducts() {
        const broadband = this.products.find((p) => p.type === "Broadband");
        return broadband ? broadband.options : [];
      },
      selectedBroadband() {
        return this.pickedProductPriceCodes["Broadband"];
      },
    }),
    ...mapGetters({
      isDefaultBroadbandProductOptionSelected:
        "isDefaultBroadbandProductOptionSelected",
    }),
    filteredSelectedProducts() {
      const orderedTypes = [
        "Energy",
        "Water",
        "Free Contents Insurance",
        "Broadband",
        "TV Licence",
      ];

      // Keep Broadband in list when no-broadband selected (so user can edit);
      // filter out _NONE for other product types
      let filteredProducts = this.selectedProducts.filter(
        (product) =>
          !product.code.includes("_NONE") || product.type === "Broadband",
      );

      const withDisplayRows = [...filteredProducts];

      if (this.shouldShowFreeContentsInsurance) {
        withDisplayRows.push({
          code: "FREE_CONTENTS_INSURANCE_DISPLAY",
          type: "Free Contents Insurance",
          title: "Contents Insurance",
          optional: false,
        });
      }

      return withDisplayRows.sort(
        (a, b) => orderedTypes.indexOf(a.type) - orderedTypes.indexOf(b.type),
      );
    },
    shouldShowFreeContentsInsurance() {
      return this.$store.getters.shouldShowFreeContentsInsurance;
    },
  },
  methods: {
    ...mapActions(["updatePreSignupLeadProductData"]),
    ...mapMutations({
      setProductOption: "SET_PRODUCT_OPTION",
      setPickedProductPrice: "SET_PICKED_PRODUCT_PRICE",
      unPickProductOption: "SET_UNPICKED_PRODUCT_OPTION",
    }),
    updateProductSelection(productOption) {
      let vm = this;
      let enabled = !productOption.enabled;

      let noneProductOption = this.$store.state.products
        .filter((product) => product.type === productOption.type)[0]
        .options.filter((option) => option.code.includes("_NONE"))[0];

      if (productOption.type === "TV Licence") {
        this.onToggleTvLicence(enabled);
      }

      if (!noneProductOption) {
        this.errorResponse =
          "Service error - Unable to update your package choices, please try again later.";
        return;
      }

      this.unPickProductOption({
        unpicked: enabled ? noneProductOption : productOption,
        picked: enabled ? productOption : noneProductOption,
      });

      productOption.enabled = enabled;

      this.updatePreSignupLeadProductData({
        offerCode: this.$store.state.discount.offer,
        affiliateCode: this.$store.state.discount.aff,
        referralCode: this.$route.query.ref,
      }).then((res) => {
        //if update failed, return changes to original
        if (!(res.status === 200 || res.status === 204)) {
          vm.unPickProductOption({
            unpicked: !enabled ? noneProductOption : productOption,
            picked: !enabled ? productOption : noneProductOption,
          });

          productOption.enabled = !enabled;

          vm.errorResponse =
            "Service error - Unable to update your package choices, please try again later.";
        }
      });
    },
    productInfoLabel(productOption) {
      switch (productOption.type) {
        case "Energy":
          return "No unexpected bills at the end of your tenancy. Make one monthly payment for Gas and Electricity with no worries of extra over-usage payments at the end of your tenancy.";
        case "Water":
          return "Water bills cover both the fresh running water into your home and the sewage system that runs through it.";
        case "TV Licence":
          return "Add a TV licence to your package to make sure your household is legally covered for watching or recording live TV (any channel) and BBC shows (including catch-up on BBC iPlayer).";
        case "Free Contents Insurance":
          return "Provided alongside your UniHomes utilities package at no additional cost to you.";
      }
    },
    productImage(productOption) {
      switch (productOption.type) {
        case "Energy":
          return "/icon-energy-blue.svg";
        case "Water":
          return "/icon-water-blue.svg";
        case "Broadband":
          return productOption.title.includes("Virgin")
            ? "/icon-virgin-media.svg"
            : "https://www.splitthebills.co.uk/wp-content/uploads//2024/06/BT-Logo.png";
        case "TV Licence":
          return "/icon-television-blue.svg";
        case "Free Contents Insurance":
          return "/icon-contents-insurance.svg";
      }
    },
    formatBroadbandDeltaPrice(productOption) {
      const currentPrice =
        productOption && productOption.price ? productOption.price.current : 0;

      if (
        !productOption ||
        !productOption.type.includes("Broadband") ||
        !this.$store.getters.defaultBroadbandProductOption
      ) {
        return Math.ceil(currentPrice || 0) / 100 || 0;
      }

      const defaultPrice = this.$store.getters.defaultBroadbandProductOption
        .price.current;
      const selectedPrice = currentPrice || 0;
      return Math.abs((selectedPrice - defaultPrice) / 100);
    },
    isCellDisabled(product) {
      if (!this.inlineEditing) {
        return false;
      }

      return product.code.includes("_NONE");
    },
    openModal(product) {
      this.selectedProduct = product || null;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async updateSelected(code) {
      if (!this.selectedProduct) return;
      this.$store.commit("SET_PRODUCT_OPTION", {
        type: this.selectedProduct.type,
        code,
      });
      const selectedOption = this.broadbandProducts.find(
        (op) => op.code === code,
      );
      if (!selectedOption) return;
      const fullProductIndex = this.products.findIndex(
        (p) => p.type === this.selectedProduct.type,
      );

      // Reset packagePrice so total recalculates from selected products
      // (reapply-discount will set it again if discount is active)
      this.$store.commit("SET_PACKAGE_PRICE", {
        fullPriceAmount: 0,
        discountedPriceAmount: 0,
      });

      // Call backend to recalc prices
      try {
        await this.updatePreSignupLeadProductData({
          offerCode: this.$store.state.discount.offer,
          affiliateCode: this.$store.state.discount.aff,
          referralCode: this.$store.state.discount.code,
        });

        if (this.$store.state.discount.isValid) {
          this.$emit("reapply-discount");
        }
        // Now totals in modal should update via computed getters
      } catch (err) {
        // console.error("Failed to update prices:", err);
      }
    },
    priceChanged(price) {
      this.$emit("price-changed", price);
    },
  },
};
</script>

<style lang="scss" scoped>
.package-panel {
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.package-panel__products {
  list-style-type: none;
  padding: 0;
  margin: 0;

  &.thin {
    margin: 0 auto;
  }
}

.inline-edit-btn {
  align-self: normal;
  margin: 3px;
  min-width: 25%;
  padding: 0;
  font-size: $form-font-size;
  border-radius: $border-radius-small;
}

.disclaimer {
  color: var(--Default-text, #3b4143);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  padding: 24px 0;
}

.package-panel__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 16px 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    border: 2px solid $grey-dark;
    background: rgba($grey-dark, 0.25);
  }
}

.package-panel__option-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0;
  background: white;
  border-radius: 8px;
}

.package-panel__option-image {
  max-width: 30px;
  max-height: 30px;
  object-fit: contain;
}

.package-panel__option-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: 2px;
}

.package-panel__option-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 27px; // keep the heigh consistent even when the info-bubble isn't present
}

.package-panel__option-name {
  margin: 0 5px 0 0;
  color: #3b4143;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  text-align: left;

  &:last-child {
    margin-bottom: 0;
  }
}

.package-panel__option-price {
  margin: 0;
  color: #3b4143;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: left;

  &-included {
    color: $brand-orange;
    font-weight: 700;
  }
}

.package-panel__free-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: left;
  background-color: $brand-orange;
  padding: 4px 8px;
  border-radius: 80px;
}

.package-panel__option-action {
  display: flex;
  align-items: center;
}

.product-option__info-btn {
  grid-row: 3;
  grid-column: 1;

  align-self: center;
  justify-self: center;

  text-align: center;
  width: 15px;
  height: 15px;
  font-size: 1.2rem;
  font-weight: 500;
  color: #6b7280;
  background-color: $white;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  border: 1px solid $brand-orange;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
}

.form-checkbox:checked {
  background-color: $brand-orange;
  border-color: $brand-orange;
}

.form-checkbox:checked::after {
  content: url("~@/assets/svg/check.svg?external");
  font-size: 2.7rem;
}

.no-broadband-icon {
  font-size: 16px;
  font-weight: 300;
  color: $brand-blue;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-broadband {
  display: flex;
  width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
  font-size: 2.75rem;
  color: $brand-blue;
}
</style>
